import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, Car, MessageSquare, CheckCircle, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { submitBooking, mockBookingSlots } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    carBrand: '',
    carModel: '',
    serviceType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.carBrand || !formData.serviceType) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Call real backend API
      const response = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(result.message || 'Booking confirmed! We\'ll contact you shortly.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          carBrand: '',
          carModel: '',
          serviceType: '',
          message: ''
        });
      } else {
        toast.error(result.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit booking. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Contact Us</span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-3 mb-4">
            Book Your Appointment
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Schedule your service appointment online or reach out to us for any questions about your German vehicle.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Booking Form */}
          <Card className="bg-white border-gray-200 p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
                
                <div>
                  <Label htmlFor="name" className="text-gray-700 mb-2 flex items-center">
                    <User className="w-4 h-4 mr-2 text-red-600" />
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="John Doe"
                    className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-red-600"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-gray-700 mb-2 flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-red-600" />
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="john@example.com"
                      className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-red-600"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-gray-700 mb-2 flex items-center">
                      <Phone className="w-4 h-4 mr-2 text-red-600" />
                      Phone *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+1 (555) 000-0000"
                      className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-red-600"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle Information */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Vehicle Information</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="carBrand" className="text-gray-700 mb-2 flex items-center">
                      <Car className="w-4 h-4 mr-2 text-red-600" />
                      Car Brand *
                    </Label>
                    <Select value={formData.carBrand} onValueChange={(value) => handleInputChange('carBrand', value)}>
                      <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900 focus:border-red-600">
                        <SelectValue placeholder="Select brand" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-50 border-gray-300">
                        <SelectItem value="bmw">BMW</SelectItem>
                        <SelectItem value="mercedes">Mercedes-Benz</SelectItem>
                        <SelectItem value="audi">Audi</SelectItem>
                        <SelectItem value="porsche">Porsche</SelectItem>
                        <SelectItem value="volkswagen">Volkswagen</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="carModel" className="text-gray-700 mb-2">
                      Car Model
                    </Label>
                    <Input
                      id="carModel"
                      type="text"
                      value={formData.carModel}
                      onChange={(e) => handleInputChange('carModel', e.target.value)}
                      placeholder="e.g., M5, C-Class"
                      className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-red-600"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="serviceType" className="text-gray-700 mb-2">
                    Service Type *
                  </Label>
                  <Select value={formData.serviceType} onValueChange={(value) => handleInputChange('serviceType', value)}>
                    <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900 focus:border-red-600">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                      <SelectItem value="engine" className="text-white hover:bg-zinc-800 focus:bg-zinc-800 focus:text-white">Engine Diagnostics & Repair</SelectItem>
                      <SelectItem value="transmission" className="text-white hover:bg-zinc-800 focus:bg-zinc-800 focus:text-white">Transmission Services</SelectItem>
                      <SelectItem value="brake" className="text-white hover:bg-zinc-800 focus:bg-zinc-800 focus:text-white">Brake System Service</SelectItem>
                      <SelectItem value="electrical" className="text-white hover:bg-zinc-800 focus:bg-zinc-800 focus:text-white">Electrical Systems</SelectItem>
                      <SelectItem value="suspension" className="text-white hover:bg-zinc-800 focus:bg-zinc-800 focus:text-white">Suspension & Steering</SelectItem>
                      <SelectItem value="maintenance" className="text-white hover:bg-zinc-800 focus:bg-zinc-800 focus:text-white">Oil Change & Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Additional Notes */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-white mb-4">Additional Information</h3>
                
                <div>
                  <Label htmlFor="message" className="text-gray-700 mb-2 flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2 text-red-600" />
                    Additional Notes
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Tell us more about your vehicle's issue or service requirements..."
                    rows={4}
                    className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-red-600 resize-none"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold rounded-lg transition-all duration-200 disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Book Appointment'}
              </Button>
            </form>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="bg-white border-gray-200 p-8 shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Get In Touch</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/20 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <div className="text-gray-600 text-sm mb-1">Call Us</div>
                    <div className="text-gray-900 font-semibold text-lg">056 667 1468</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/20 p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <div className="text-gray-600 text-sm mb-1">Email Us</div>
                    <div className="text-gray-900 font-semibold text-lg">info@thinkmechanicdxb.com</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/20 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <div className="text-gray-600 text-sm mb-1">Location</div>
                    <div className="text-gray-900 font-semibold">Ras Al Khor Industrial Area 1</div>
                    <div className="text-gray-900 font-semibold">Dubai, UAE</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/20 p-3 rounded-lg">
                    <Clock className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <div className="text-gray-600 text-sm mb-1">Business Hours</div>
                    <div className="text-gray-900 font-semibold">Saturday - Thursday</div>
                    <div className="text-gray-900 font-semibold">8:00 AM - 6:00 PM</div>
                    <div className="text-gray-600 font-semibold text-sm mt-1">Friday: Closed</div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-gradient-to-br from-red-600 to-red-700 border-0 p-8 text-white">
              <div className="flex items-start space-x-4 mb-4">
                <CheckCircle className="w-8 h-8 flex-shrink-0" />
                <div>
                  <h4 className="text-xl font-semibold mb-2">What Happens Next?</h4>
                  <ul className="space-y-2 text-white/90">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                      We'll confirm your appointment within 2 hours
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                      Receive a detailed service estimate
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                      Get updates throughout the service process
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
